// An access point's own radio settings on its page, with who set each channel
// (ADR-0338 decision 2; ADR-0253 decision 1C, #1382).
//
// **A channel has two possible authors**: a person designing the site, and a
// service principal such as Beaconwright's runtime principal, which moves a
// channel at runtime through `/v1`. The overlay read names which one last
// changed each band's channel, and this says it in those words. A person who
// changes a channel a service set is recorded and shown, never refused
// (decision 3). The overrides are edited on the site's channel map; this is a
// read and offers no act.
import { api } from "../../api/client";
import { useQuery } from "../../api/hooks";
import type { components } from "../../api/schema";
import { Card, Info, ProblemBox } from "../../ui";
import { wordFor } from "../../api/openEnum";

type Overlay = components["schemas"]["RadioOverlay"];

// The channel map's words for a band. Not imported from it: that module
// writes overlays, and this page is Operate's, which imports no write
// (ADR-0149; src/shell/operate.test.ts).
const BAND_LABEL: Record<string, string> = { "2G": "2.4 GHz", "5G": "5 GHz", "6G": "6 GHz" };

/** Who set a band's channel, in the words ADR-0338 decision 2 rules. */
export function channelAuthorWords(o: Overlay): string | undefined {
  if (o.channel === undefined) return undefined;
  const by = o.channel_set_by;
  // Restates ADR-0338 decision 2 — a channel set before authorship was recorded names nobody.
  if (!by) return "set before who set it was recorded";
  const who = by.name ?? by.id;
  const when = new Date(by.at).toLocaleString();
  return by.kind === "service" ? `set by ${who} (service), ${when}` : `set by ${who}, ${when}`;
}

export default function RadioOverrides({ serial }: { serial: string }) {
  const q = useQuery(() => api.GET("/devices/{serial}/radio-overlays", { params: { path: { serial } } }), [serial]);
  if (q.status === "error") return <Card className="panel" title="Its own radio settings"><ProblemBox problem={q.problem} /></Card>;
  const radios = q.data?.radios ?? [];
  if (radios.length === 0) return null;
  return (
    <Card className="panel" title="Its own radio settings"
      head={<Info label="About its own radio settings">Where this access point differs from its site&rsquo;s radios. They are changed on the site&rsquo;s channel map.</Info>}>
      <ul>
        {radios.map((r) => (
          <li key={r.band} data-band={r.band}>
            {wordFor(BAND_LABEL, r.band)}
            {r.channel !== undefined && <>: channel {r.channel} <span className="dim">— {channelAuthorWords(r)}</span></>}
            {r.channel === undefined && <span className="dim">: channel follows the site</span>}
          </li>
        ))}
      </ul>
    </Card>
  );
}
